
import OpenAI from 'openai';
import { supabase } from '@/lib/supabase/client';

interface GPT5Analysis {
  understanding: string;
  direct_matches: Array<{
    name: string;
    reason: string;
    confidence: number;
  }>;
  search_parameters: {
    cuisine?: string;
    price_range?: string;
    vibe?: string[];
    special_requirements?: string[];
  };
  needs_web_search: boolean;
  explanation: string;
}

interface EnrichedVenue {
  id?: string;
  place_id?: string;
  name: string;
  place_name?: string; // Alias for compatibility

  address?: string;
  latitude: number;
  longitude: number;
  neighborhood?: string;
  city?: string;

  cuisine_type?: string;
  category?: string;
  price_level?: number;
  rating?: number;
  user_ratings_total?: number;
  detected_neighborhood?: string;
  business_status?: string;

  photos?: string[];
  photo?: string; // First photo for cards
  website?: string;
  phone?: string;

  opening_hours?: any;
  open_now?: boolean;

  gpt5_reason?: string;
  gpt5_confidence?: number;
  curatorScore?: number;
  attribution?: string;
  distance?: number;

  source: 'curated' | 'parametric' | 'google' | 'web';
}

export interface SearchIntent {
  original: string;
  isNearMe: boolean;
  isOpenNow: boolean;
  isTrending: boolean;
  isDateNight: boolean;
  isCheap: boolean;
  isLike: boolean;
  cuisine: string | null;
  vibe: string | null;
  priceLevel: number | null;
}

export interface SearchResult {
  curated: EnrichedVenue[];
  discovered: EnrichedVenue[];
  intent: SearchIntent;
  total: number;
}

/**
 * DISABLED: Legacy client-side search service.
 * The app now uses Agent tools (`search_all`) and voice pipeline.
 * Keep exported for type compatibility in any lingering imports.
 */
export class UnifiedSearchService {
  private openai: OpenAI | null = null;
  private placesService: google.maps.places.PlacesService | null = null;
  private initialized = false;
  private curatorContext: any = null;

  async initialize() {
    if (this.initialized) return;

    // Skip OpenAI in browser - needs backend proxy to avoid CORS
    // OpenAI API calls should go through netlify functions
    // For now, we'll use basic search without GPT-5 analysis


    if (typeof google !== 'undefined' && !this.placesService) {
      const map = new google.maps.Map(document.createElement('div'));
      this.placesService = new google.maps.places.PlacesService(map);
    }

    this.initialized = true;
  }

  async search(
    query: string,
    city: string,
    userLocation?: { lat: number; lng: number } | null
  ): Promise<SearchResult> {
    await this.initialize();

    const gptAnalysis = await this.analyzeWithGPT5(query, city);

    const curatedMatches = await this.searchCuratedVenues(
      gptAnalysis,
      city
    );

    const unmatchedSuggestions = gptAnalysis.direct_matches.filter(
      suggestion => !curatedMatches.find(
        curated => this.normalizeVenueName(curated.place_name || curated.name) ===
                   this.normalizeVenueName(suggestion.name)
      )
    );

    const placesEnriched = await this.enrichWithGooglePlaces(
      unmatchedSuggestions,
      city
    );

    const discoveredVenues = [...placesEnriched];

    if (discoveredVenues.length < 5 && gptAnalysis.needs_web_search) {
      const webResults = await this.performWebSearch(
        query,
        city,
        gptAnalysis.search_parameters
      );
      discoveredVenues.push(...webResults);
    }

    const allVenues = [...curatedMatches, ...discoveredVenues];
    if (userLocation) {
      allVenues.forEach(venue => {
        venue.distance = this.calculateDistance(
          { lat: venue.latitude, lng: venue.longitude },
          userLocation
        );
      });
    }

    const sortedCurated = this.rankResults(curatedMatches, gptAnalysis, userLocation);
    const sortedDiscovered = this.rankResults(discoveredVenues, gptAnalysis, userLocation);

    return {
      curated: sortedCurated,
      discovered: sortedDiscovered,
      intent: this.extractIntent(query),
      total: sortedCurated.length + sortedDiscovered.length
    };
  }

  private async basicSearch(
    query: string,
    city: string,
    userLocation?: { lat: number; lng: number } | null
  ): Promise<SearchResult> {
    const intent = this.extractIntent(query);

    const { data: curated } = await supabase
      .from('recommendations')
      .select('*')
      .eq('city', city)
      .eq('business_status', 'OPERATIONAL')
      .ilike('place_name', `%${query}%`)
      .limit(20);

    const curatedVenues: EnrichedVenue[] = (curated || []).map(v => ({
      ...v,
      name: v.place_name,
      source: 'curated' as const,
      curatorScore: 1.0,
      attribution: 'Curated by Rendezvous'
    }));

    let discovered: EnrichedVenue[] = [];
    if (this.placesService && userLocation) {
      discovered = await this.searchGooglePlaces(query, city, userLocation);
    }

    // OPTION: If a Vectorize endpoint is configured, prime an ANN query for speed (non-blocking)
    try {
      const workerUrl = (import.meta as any).env?.VITE_VECTORIZE_URL || (import.meta as any).env?.VITE_REMY_WORKER_URL;
      const _embedModel = (import.meta as any).env?.VITE_EMBED_MODEL || 'text-embedding-3-small';
      if (workerUrl && typeof fetch !== 'undefined') {
        // Assume embeddings are generated elsewhere; this is a placeholder hook
        // You can swap in an embeddings fetch here and POST to /vectorize
        // fetch(`${workerUrl.replace(/\/$/,'')}/vectorize`, { method:'POST', body: JSON.stringify({ city, embedding, topK: 16 }) })
      }
    } catch {}

    return {
      curated: curatedVenues,
      discovered,
      intent,
      total: curatedVenues.length + discovered.length
    };
  }

  private async analyzeWithGPT5(query: string, city: string): Promise<GPT5Analysis> {
    void city;
    return this.getFallbackAnalysis(query);
  }

  private getFallbackAnalysis(query: string): GPT5Analysis {
    return {
      understanding: query,
      direct_matches: [],
      search_parameters: this.extractBasicParameters(query),
      needs_web_search: true,
      explanation: 'Searching for venues matching your query'
    };
  }

  private async searchCuratedVenues(
    analysis: GPT5Analysis,
    city: string
  ): Promise<EnrichedVenue[]> {
    // Guard: avoid returning the entire city when we don't have specific signals
    const params = analysis.search_parameters || {} as any;
    const hasAnyParam = !!(params?.cuisine || (Array.isArray(params?.vibe) && params.vibe.length > 0));
    const hasDirectMatches = Array.isArray(analysis.direct_matches) && analysis.direct_matches.length > 0;
    const rawQuery = (analysis.understanding || '').trim();
    const shouldNameFilter = rawQuery.length > 0 && rawQuery.length <= 64; // short name-like queries only

    // If we have no direct name matches and no semantic params, don't pull the whole table
    if (!hasDirectMatches && !hasAnyParam && !shouldNameFilter) {
      return [];
    }

    let query = supabase
      .from('recommendations')
      .select('*')
      .eq('city', city)
      .eq('business_status', 'OPERATIONAL');

    if (hasDirectMatches) {
      const nameConditions = analysis.direct_matches
        .map(m => `place_name.ilike.%${m.name}%`)
        .join(',');
      query = query.or(nameConditions);
    } else if (shouldNameFilter && !hasAnyParam) {
      // Light name filter for short queries with no cuisine/vibe signal — avoid ANDing against semantic filters
      query = query.ilike('place_name', `%${rawQuery}%`);
    }

    if (params.cuisine) {
      query = query.ilike('cuisine_type', `%${params.cuisine}%`);
    }

    if (params.vibe?.length) {
      const vibeConditions = params.vibe
        .map(v => `vibe.ilike.%${v}%`)
        .join(',');
      query = query.or(vibeConditions);
    }

    const { data, error } = await query.limit(20);

    if (error) {
      console.error('Curated search error:', error);
      return [];
    }

    return (data || []).map(venue => {
      const gptMatch = analysis.direct_matches.find(m =>
        this.normalizeVenueName(venue.place_name).includes(
          this.normalizeVenueName(m.name)
        )
      );

      return {
        ...venue,
        name: venue.place_name,
        latitude: venue.latitude,
        longitude: venue.longitude,
        photo: venue.photo_url,
        source: 'curated' as const,
        gpt5_reason: gptMatch?.reason,
        gpt5_confidence: gptMatch?.confidence,
        curatorScore: 1.0,
        attribution: gptMatch?.reason || 'Curated by Rendezvous'
      };
    });
  }

  private async enrichWithGooglePlaces(
    suggestions: Array<{ name: string; reason: string; confidence: number }>,
    city: string
  ): Promise<EnrichedVenue[]> {
    if (!this.placesService || suggestions.length === 0) return [];

    const enrichedVenues: EnrichedVenue[] = [];

    for (const suggestion of suggestions) {
      try {
        const placeData = await this.findPlaceDetails(suggestion.name, city);

        if (placeData) {
          enrichedVenues.push({
            ...placeData,
            gpt5_reason: suggestion.reason,
            gpt5_confidence: suggestion.confidence,
            curatorScore: suggestion.confidence * 0.8,
            attribution: suggestion.reason,
            source: 'parametric'
          });
        }
      } catch (error) {
        console.error(`Failed to enrich ${suggestion.name}:`, error);
      }
    }

    return enrichedVenues;
  }

  private async searchGooglePlaces(
    query: string,
    city: string,
    location: { lat: number; lng: number }
  ): Promise<EnrichedVenue[]> {
    if (!this.placesService) return [];

    return new Promise((resolve) => {
      const request = {
        query: `${query} restaurant ${city}`,
        location: new google.maps.LatLng(location.lat, location.lng),
        radius: 5000
      };

      this.placesService!.textSearch(request, (results, status) => {
        if (status === 'OK' && results) {
          const venues: EnrichedVenue[] = results.slice(0, 10).map(place => ({
            place_id: place.place_id,
            name: place.name || '',
            place_name: place.name,
            address: place.formatted_address,
            latitude: place.geometry?.location?.lat() || 0,
            longitude: place.geometry?.location?.lng() || 0,
            rating: place.rating,
            price_level: place.price_level,
            user_ratings_total: place.user_ratings_total,
            photo: place.photos?.[0]?.getUrl({ maxWidth: 400 }),
            photos: place.photos?.slice(0, 3).map(p =>
              p.getUrl({ maxWidth: 800 })
            ),
            source: 'google' as const,
            curatorScore: this.calculateCuratorScore(place),
            attribution: this.generateAttribution(place)
          }));
          resolve(venues);
        } else {
          resolve([]);
        }
      });
    });
  }

  private async findPlaceDetails(
    venueName: string,
    city: string
  ): Promise<EnrichedVenue | null> {
    return new Promise((resolve) => {
      if (!this.placesService) {
        resolve(null);
        return;
      }

      const findRequest = {
        query: `${venueName} restaurant ${city}`,
        fields: ['place_id', 'name', 'geometry']
      };

      (this.placesService as any).findPlaceFromText(findRequest, (results: any, status: string) => {
        if (status === 'OK' && results?.[0]) {
          const place = results[0];

          const detailsRequest = {
            placeId: place.place_id!,
            fields: [
              'name', 'formatted_address', 'geometry', 'rating',
              'price_level', 'photos', 'opening_hours', 'website',
              'formatted_phone_number', 'types', 'user_ratings_total'
            ]
          };

          this.placesService!.getDetails(detailsRequest, (details, detailStatus) => {
            if (detailStatus === 'OK' && details) {
              resolve({
                place_id: details.place_id,
                name: details.name || venueName,
                place_name: details.name || venueName,
                address: details.formatted_address,
                latitude: details.geometry?.location?.lat() || 0,
                longitude: details.geometry?.location?.lng() || 0,
                rating: details.rating,
                price_level: details.price_level,
                user_ratings_total: details.user_ratings_total,
                photo: details.photos?.[0]?.getUrl({ maxWidth: 400 }),
                photos: details.photos?.slice(0, 3).map(p =>
                  p.getUrl({ maxWidth: 800 })
                ),
                website: details.website,
                phone: details.formatted_phone_number,
                opening_hours: details.opening_hours?.weekday_text,
                open_now: details.opening_hours?.isOpen(),
                source: 'google' as const
              });
            } else {
              resolve(null);
            }
          });
        } else {
          resolve(null);
        }
      });
    });
  }

  private async performWebSearch(
    _query: string,
    _city: string,
    _parameters: any
  ): Promise<EnrichedVenue[]> {
    // Web search would be implemented here
    // For now, return empty array
    return [];
  }

  private calculateCuratorScore(place: any): number {
    let score = 0.5;

    if (place.rating >= 4.5) score += 0.2;
    else if (place.rating >= 4.3) score += 0.1;

    if (place.user_ratings_total > 500) score += 0.1;

    const chains = ['starbucks', 'mcdonalds', 'subway', 'chipotle'];
    if (chains.some(chain => place.name?.toLowerCase().includes(chain))) {
      score -= 0.5;
    }

    return Math.max(0, Math.min(1, score));
  }

  private generateAttribution(place: any): string {
    const score = this.calculateCuratorScore(place);

    if (score > 0.7) return "Highly rated local favorite";
    if (score > 0.5) return "Popular neighborhood spot";
    return "Local option";
  }

  private calculateDistance(
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const R = 6371000; // Earth radius in meters
    const φ1 = point1.lat * Math.PI / 180;
    const φ2 = point2.lat * Math.PI / 180;
    const Δφ = (point2.lat - point1.lat) * Math.PI / 180;
    const Δλ = (point2.lng - point1.lng) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }

  private rankResults(
    venues: EnrichedVenue[],
    analysis: GPT5Analysis,
    userLocation?: { lat: number; lng: number } | null
  ): EnrichedVenue[] {
    return venues.sort((a, b) => {
      let scoreA = 0, scoreB = 0;

      scoreA += (a.gpt5_confidence || 0) * 100;
      scoreB += (b.gpt5_confidence || 0) * 100;

      scoreA += (a.curatorScore || 0) * 50;
      scoreB += (b.curatorScore || 0) * 50;

      const sourceScores = { curated: 40, parametric: 30, google: 20, web: 10 };
      scoreA += sourceScores[a.source] || 0;
      scoreB += sourceScores[b.source] || 0;

      scoreA += (a.rating || 0) * 5;
      scoreB += (b.rating || 0) * 5;

      if (userLocation && a.distance !== undefined && b.distance !== undefined) {
        scoreA -= a.distance / 100;
        scoreB -= b.distance / 100;
      }

      return scoreB - scoreA;
    });
  }

  private normalizeVenueName(name: string): string {
    return name.toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .trim();
  }

  private extractIntent(query: string): SearchIntent {
    const q = query.toLowerCase();

    return {
      original: query,
      isNearMe: q.includes('near me') || q.includes('close by'),
      isOpenNow: q.includes('open now') || q.includes('open late'),
      isTrending: q.includes('trending') || q.includes('popular'),
      isDateNight: q.includes('date') || q.includes('romantic'),
      isCheap: q.includes('cheap') || q.includes('budget'),
      isLike: q.includes('like ') || q.includes('similar to'),
      cuisine: this.extractCuisine(q),
      vibe: this.extractVibe(q),
      priceLevel: this.extractPriceLevel(q)
    };
  }

  private extractBasicParameters(query: string): any {
    const q = query.toLowerCase();
    return {
      cuisine: this.extractCuisine(q),
      price_range: this.extractPriceRange(q),
      vibe: this.extractVibes(q),
      special_requirements: []
    };
  }

  private extractCuisine(query: string): string | null {
    const cuisines = [
      'italian', 'french', 'japanese', 'chinese', 'thai',
      'indian', 'mexican', 'american', 'mediterranean', 'korean',
      'vietnamese', 'spanish', 'greek', 'seafood', 'sushi'
    ];
    return cuisines.find(c => query.includes(c)) || null;
  }

  /**
   * Each entry: [canonical, ...synonyms]. All tokens in a group are ORed
   * together so rows tagged with any member are matched regardless of which
   * NL word triggered the group.  Matching uses word boundaries to avoid
   * substring false-positives (e.g. 'date' inside 'update', 'hip' in 'chip').
   */
  private static readonly VIBE_GROUPS: ReadonlyArray<ReadonlyArray<string>> = [
    ['romantic', 'date'],
    ['casual', 'relaxed'],
    ['trendy', 'hip'],
    ['cozy', 'intimate'],
    ['lively', 'vibrant', 'energetic'],
  ];

  // Inflected forms restored for the two-sided `\b${token}\b` regex. The
  // word-boundary match added in 84f3c9c5 dropped plurals/adverbs that the old
  // `query.includes(token)` had matched for free. Only realistic vibe phrasings
  // a user would actually utter are listed here; the rest of the flipped forms
  // are intentionally left suppressed:
  //   hip: 'hipster'/'hippie'/'hips' — intentional suppression of 84f3c9c5.
  //   date: 'dated' — adjective meaning "outdated", not romantic intent; the
  //     old `includes('date')` match was a false positive the commit dropped.
  //   'romantics'/'casuals' — plural nouns, not vibe queries.
  //   'relaxedly' — rare adverb no user utters as a vibe query.
  //   cozy/lively group adverbs — groups introduced by 84f3c9c5; never matched
  //   before, so excluding them is not a regression.
  private static readonly VIBE_INFLECTIONS: Readonly<Record<string, ReadonlyArray<string>>> = {
    date: ['dates', 'dating'],
    romantic: ['romantically'],
    casual: ['casually'],
  };

  private matchesVibeGroup(query: string, group: ReadonlyArray<string>): boolean {
    return group.some(token => {
      const forms = [token, ...(UnifiedSearchService.VIBE_INFLECTIONS[token] ?? [])];
      return forms.some(form => new RegExp(`\\b${form}\\b`).test(query));
    });
  }

  private extractVibe(query: string): string | null {
    for (const group of UnifiedSearchService.VIBE_GROUPS) {
      if (this.matchesVibeGroup(query, group)) return group[0];
    }
    return null;
  }

  private extractVibes(query: string): string[] {
    const vibes: string[] = [];
    for (const group of UnifiedSearchService.VIBE_GROUPS) {
      if (this.matchesVibeGroup(query, group)) {
        // Push every member of the group so canonical-only and synonym-only
        // rows are both matched by the PostgREST OR filter.
        vibes.push(...group);
      }
    }
    return vibes;
  }

  private extractPriceLevel(query: string): number | null {
    if (query.includes('cheap') || query.includes('budget')) return 1;
    if (query.includes('affordable')) return 2;
    if (query.includes('upscale') || query.includes('fancy')) return 3;
    if (query.includes('luxury')) return 4;
    return null;
  }

  private extractPriceRange(query: string): string {
    if (query.includes('cheap')) return '$';
    if (query.includes('affordable')) return '$$';
    if (query.includes('upscale')) return '$$$';
    if (query.includes('luxury')) return '$$$$';
    return '$$';
  }
}
